import { useState, useEffect, useMemo } from "react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "./ui/context-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Folder,
  FileText,
  ChevronRight,
  ChevronDown,
  Plus,
  FolderPlus,
  Pin,
  Trash2,
  Pencil,
  Loader2,
  ArrowUpDown,
  GripVertical,
} from "lucide-react";

const FileTreeItem = (props) => {
  const {
    item,
    level = 0,
    selectedFile,
    onSelectFile,
    onDeleteFile,
    onRenameFile,
    onTogglePin,
    onMoveFile,
    onCreateFile,
    expandedFolders,
    toggleFolder,
    draggedItem,
    setDraggedItem,
    onOpenNewItemDialog,
    sortBy,
    updateCustomOrder,
  } = props;
  
  const [renameDialog, setRenameDialog] = useState(false);
  const [newName, setNewName] = useState(item.name.replace(".md", ""));
  
  // NOU: Acum ținem minte dacă vrei să-l pui 'top' (între), 'bottom' (între) sau 'inside' (în folder)
  const [dropPosition, setDropPosition] = useState(null); 
  
  const isSelected = selectedFile?.path === item.path;
  const isExpanded = expandedFolders.has(item.path);

  const handleRename = () => {
    if (newName.trim() && newName !== item.name.replace(".md", "")) {
      onRenameFile(item.path, newName);
    }
    setRenameDialog(false);
  };

  const handleDragStart = (e) => {
    e.stopPropagation();
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", item.path);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedItem || draggedItem.path === item.path) return;

    // Calculăm unde anume pe element se află mouse-ul
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const threshold = 6; // zona de 6px sus/jos pentru a pune "între" foldere

    let pos = "inside";
    if (item.type === "folder") {
      if (y < threshold) pos = "top";
      else if (y > rect.height - threshold) pos = "bottom";
    } else {
      // Peste un fișier nu poți pune ÎNĂUNTRU, deci e mereu pus LÂNGĂ el (sus/jos)
      pos = y < rect.height / 2 ? "top" : "bottom";
    }

    setDropPosition(pos);
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragLeave = (e) => {
    e.stopPropagation();
    setDropPosition(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation(); 
    
    const currentPos = dropPosition;
    setDropPosition(null);
    
    if (!draggedItem || draggedItem.path === item.path) {
      setDraggedItem(null);
      return;
    }

    const draggedParentPath = draggedItem.path.includes("/") ? draggedItem.path.substring(0, draggedItem.path.lastIndexOf("/")) : "";
    const itemParentPath = item.path.includes("/") ? item.path.substring(0, item.path.lastIndexOf("/")) : "";

    // 1. REORDONARE CUSTOM ÎN ACELAȘI FOLDER
    if (sortBy === "custom" && draggedParentPath === itemParentPath) {
      updateCustomOrder(draggedItem.path, item.path, currentPos === 'top' ? 'before' : 'after');
    } 
    else {
      // 2. MUTARE NORMALĂ: 
      // Dacă e "inside" pe un folder, îl băgăm ÎN acel folder
      if (currentPos === "inside" && item.type === "folder") {
        if (!draggedItem.path.startsWith(item.path + "/")) {
          onMoveFile(draggedItem.path, item.path);
        }
      } 
      // 3. Altfel (top/bottom, deci "între" foldere), îl băgăm în Root sau în același părinte
      else {
        if (itemParentPath !== draggedParentPath) {
          onMoveFile(draggedItem.path, itemParentPath);
        }
      }
    }
    
    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDropPosition(null);
  };

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div
            className={cn(
              "file-tree-item group transition-all cursor-pointer relative",
              isSelected && "active",
              // Efecte vizuale inteligente:
              dropPosition === "inside" && "bg-primary/20 ring-1 ring-primary",
              dropPosition === "top" && "before:absolute before:top-0 before:left-0 before:right-0 before:h-[2px] before:bg-primary before:z-10",
              dropPosition === "bottom" && "after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-primary after:z-10",
              draggedItem?.path === item.path && "opacity-50"
            )}
            style={{ paddingLeft: `${level * 12 + 8}px` }}
            onClick={() => {
              if (item.type === "folder") toggleFolder(item.path);
              else onSelectFile(item.path);
            }}
            draggable="true"
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
            data-testid={`file-tree-item-${item.path}`}
          >
            <GripVertical className="w-3 h-3 opacity-0 group-hover:opacity-50 flex-shrink-0 mr-1" />
            {item.type === "folder" ? (
              <>
                <span className="text-muted-foreground">
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </span>
                <Folder className="w-4 h-4 text-primary/70" />
              </>
            ) : (
              <>
                <span className="w-4" />
                <FileText className="w-4 h-4 text-muted-foreground" />
              </>
            )}
            <span className="flex-1 truncate text-sm">{item.name}</span>
            
            <div className="flex items-center gap-0.5">
              {item.is_pinned && <Pin className="w-3 h-3 text-primary" />}
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 opacity-0 group-hover:opacity-100"
                onClick={(e) => { e.stopPropagation(); onTogglePin(item.path); }}
                title={item.is_pinned ? "Unpin" : "Pin"}
              >
                <Pin className={cn("w-3 h-3", item.is_pinned && "text-primary")} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 opacity-0 group-hover:opacity-100"
                onClick={(e) => { e.stopPropagation(); setRenameDialog(true); }}
                title="Rename"
              >
                <Pencil className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </ContextMenuTrigger>
        
        <ContextMenuContent className="w-48">
          <ContextMenuItem onClick={() => setRenameDialog(true)}>
            <Pencil className="w-4 h-4 mr-2" />
            Rename
          </ContextMenuItem>
          <ContextMenuItem onClick={() => onTogglePin(item.path)}>
            <Pin className="w-4 h-4 mr-2" />
            {item.is_pinned ? "Unpin" : "Pin to Top"}
          </ContextMenuItem>
          
          <ContextMenuSeparator />
          
          <ContextMenuItem onClick={() => {
            const targetPath = item.type === "folder" ? item.path : (item.path.includes("/") ? item.path.substring(0, item.path.lastIndexOf("/")) : "");
            if (item.type === "folder" && !isExpanded) toggleFolder(item.path);
            onOpenNewItemDialog("file", targetPath);
          }}>
            <FileText className="w-4 h-4 mr-2" />
            New Note
          </ContextMenuItem>
          
          <ContextMenuItem onClick={() => {
            const targetPath = item.type === "folder" ? item.path : (item.path.includes("/") ? item.path.substring(0, item.path.lastIndexOf("/")) : "");
            if (item.type === "folder" && !isExpanded) toggleFolder(item.path);
            onOpenNewItemDialog("folder", targetPath);
          }}>
            <FolderPlus className="w-4 h-4 mr-2" />
            New Folder
          </ContextMenuItem>

          <ContextMenuSeparator />
          <ContextMenuItem
            onClick={() => onDeleteFile(item.path)}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      {item.type === "folder" && isExpanded && item.children && (
        <div className="animate-slide-in">
          {item.children.map((child) => (
            <FileTreeItem
              key={child.path}
              item={child}
              level={level + 1}
              selectedFile={selectedFile}
              onSelectFile={onSelectFile}
              onDeleteFile={onDeleteFile}
              onRenameFile={onRenameFile}
              onTogglePin={onTogglePin}
              onMoveFile={onMoveFile}
              onCreateFile={onCreateFile}
              expandedFolders={expandedFolders}
              toggleFolder={toggleFolder}
              draggedItem={draggedItem}
              setDraggedItem={setDraggedItem}
              onOpenNewItemDialog={onOpenNewItemDialog}
              sortBy={sortBy}
              updateCustomOrder={updateCustomOrder}
            />
          ))}
        </div>
      )}

      <Dialog open={renameDialog} onOpenChange={setRenameDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename {item.type === "folder" ? "Folder" : "Note"}</DialogTitle>
            <DialogDescription>Enter a new name for the {item.type === "folder" ? "folder" : "note"}.</DialogDescription>
          </DialogHeader>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Enter new name"
            onKeyDown={(e) => e.key === "Enter" && handleRename()}
            data-testid="rename-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleRename} data-testid="rename-confirm-button">
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Extrage timpul in milisecunde pentru sortare
const getTime = (item, type) => {
  const created = item.created_at || item.createdAt || item.date_created || 0;
  const modified = item.modified_at || item.updated_at || item.updatedAt || created;
  const dateStr = type === 'created' ? created : modified;
  const dateObj = new Date(dateStr);
  return isNaN(dateObj.getTime()) ? 0 : dateObj.getTime();
};

const sortFiles = (files, sortBy, customOrder = []) => {
  const sorted = [...files].sort((a, b) => {
    // 1. Pinned
    if (a.is_pinned && !b.is_pinned) return -1;
    if (!a.is_pinned && b.is_pinned) return 1;
    
    // 2. Custom Order
    if (sortBy === "custom") {
      const idxA = customOrder.indexOf(a.path);
      const idxB = customOrder.indexOf(b.path);
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1; 
    }

    // 3. Foldere prima data
    if (a.type === "folder" && b.type !== "folder") return -1;
    if (a.type !== "folder" && b.type === "folder") return 1;
    
    // 4. Default Orders
    switch (sortBy) {
      case "name-asc": return a.name.localeCompare(b.name);
      case "name-desc": return b.name.localeCompare(a.name);
      case "date-created-newest": return getTime(b, 'created') - getTime(a, 'created');
      case "date-created-oldest": return getTime(a, 'created') - getTime(b, 'created');
      case "date-modified": return getTime(b, 'modified') - getTime(a, 'modified');
      case "custom": return a.name.localeCompare(b.name);
      default: return a.name.localeCompare(b.name);
    }
  });
  
  return sorted.map(item => {
    if (item.type === "folder" && item.children) {
      return { ...item, children: sortFiles(item.children, sortBy, customOrder) };
    }
    return item;
  });
};

export const Sidebar = ({
  files,
  selectedFile,
  isOpen,
  isLoading,
  onSelectFile,
  onCreateFile,
  onDeleteFile,
  onRenameFile,
  onMoveFile,
  onTogglePin,
  children,
}) => {
  const [expandedFolders, setExpandedFolders] = useState(() => {
    try {
      const saved = localStorage.getItem("notekeeper_expanded_folders");
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch (e) {
      return new Set();
    }
  });

  const [customOrder, setCustomOrder] = useState(() => {
    try {
      const saved = localStorage.getItem("notekeeper_custom_order");
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  
  const [newItemDialog, setNewItemDialog] = useState({ open: false, type: "file", parentPath: "" });
  const [newItemName, setNewItemName] = useState("");
  const [sortBy, setSortBy] = useState("name-asc");
  const [draggedItem, setDraggedItem] = useState(null);

  const toggleFolder = (path) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      localStorage.setItem("notekeeper_expanded_folders", JSON.stringify(Array.from(next)));
      return next;
    });
  };

  const updateCustomOrder = (draggedPath, targetPath, position = 'after') => {
    setCustomOrder((prev) => {
      let newOrder = [...prev];
      if (!newOrder.includes(draggedPath)) newOrder.push(draggedPath);
      if (!newOrder.includes(targetPath)) newOrder.push(targetPath);

      newOrder = newOrder.filter((p) => p !== draggedPath);
      const targetIdx = newOrder.indexOf(targetPath);
      
      // Inseram deasupra (before) sau dedesubt (after)
      newOrder.splice(position === 'before' ? Math.max(0, targetIdx) : targetIdx + 1, 0, draggedPath);

      localStorage.setItem("notekeeper_custom_order", JSON.stringify(newOrder));
      return newOrder;
    });
  };

  const handleCreateItem = () => {
    if (newItemName.trim()) {
      onCreateFile(newItemName, newItemDialog.parentPath, newItemDialog.type === "folder");
      setNewItemName("");
      setNewItemDialog({ open: false, type: "file", parentPath: "" });
    }
  };

  const sortedFiles = useMemo(() => sortFiles(files, sortBy, customOrder), [files, sortBy, customOrder]);

  if (!isOpen) return null;

  return (
    <aside
      className="w-56 md:w-64 border-r border-border bg-background backdrop-blur-xl flex flex-col shrink-0 h-full"
      data-testid="sidebar"
    >
      <div 
        className={cn(
          "p-3 md:p-4 border-b border-border transition-colors",
          draggedItem && "bg-primary/5 border-b-primary/50 border-dashed"
        )}
        onDragOver={(e) => {
          e.preventDefault();
          if (draggedItem) e.dataTransfer.dropEffect = "move";
        }}
        onDrop={(e) => {
          e.preventDefault();
          if (draggedItem) {
            const draggedParent = draggedItem.path.includes("/") ? draggedItem.path.substring(0, draggedItem.path.lastIndexOf("/")) : "";
            if (draggedParent !== "") onMoveFile(draggedItem.path, "");
            setDraggedItem(null);
          }
        }}
      >
        <div className="flex items-center justify-between mb-2 md:mb-3">
          <h2 className="text-xs md:text-sm font-medium text-muted-foreground uppercase tracking-wider">
            {draggedItem ? "Drop to Root" : "Files"}
          </h2>
          <div className="flex items-center gap-0.5 md:gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 md:h-7 md:w-7"
              onClick={() => setNewItemDialog({ open: true, type: "file", parentPath: "" })}
              data-testid="new-file-button"
              title="New Note"
            >
              <Plus className="w-3 h-3 md:w-4 md:h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 md:h-7 md:w-7"
              onClick={() => setNewItemDialog({ open: true, type: "folder", parentPath: "" })}
              data-testid="new-folder-button"
              title="New Folder"
            >
              <FolderPlus className="w-3 h-3 md:w-4 md:h-4" />
            </Button>
          </div>
        </div>
        
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="h-7 md:h-8 text-xs">
            <ArrowUpDown className="w-3 h-3 mr-1 md:mr-2" />
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="custom">Custom Order (Drag & Drop)</SelectItem>
            <SelectItem value="name-asc">Name (A-Z)</SelectItem>
            <SelectItem value="name-desc">Name (Z-A)</SelectItem>
            <SelectItem value="date-modified">Recently Modified</SelectItem>
            <SelectItem value="date-created-newest">Created (Newest)</SelectItem>
            <SelectItem value="date-created-oldest">Created (Oldest)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <ScrollArea className="flex-1">
        <div className="py-2 min-h-full flex flex-col">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : sortedFiles.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-muted-foreground">No files yet</p>
              <Button
                variant="link"
                size="sm"
                onClick={() => setNewItemDialog({ open: true, type: "file", parentPath: "" })}
                className="mt-2"
              >
                Create your first note
              </Button>
            </div>
          ) : (
            sortedFiles.map((item) => (
              <FileTreeItem
                key={item.path}
                item={item}
                selectedFile={selectedFile}
                onSelectFile={onSelectFile}
                onDeleteFile={onDeleteFile}
                onRenameFile={onRenameFile}
                onTogglePin={onTogglePin}
                onMoveFile={onMoveFile}
                onCreateFile={onCreateFile}
                expandedFolders={expandedFolders}
                toggleFolder={toggleFolder}
                draggedItem={draggedItem}
                setDraggedItem={setDraggedItem}
                onOpenNewItemDialog={(type, parentPath) => setNewItemDialog({ open: true, type, parentPath })}
                sortBy={sortBy}
                updateCustomOrder={updateCustomOrder}
              />
            ))
          )}
          
          {/* Zona invizibilă la final ca să poți arunca ușor la finalul listei/în root */}
          <div 
            className="flex-1 min-h-[100px] w-full mt-4 border-t border-transparent transition-colors"
            onDragOver={(e) => {
              e.preventDefault();
              if (draggedItem) e.currentTarget.classList.add("bg-primary/5", "border-primary/30");
            }}
            onDragLeave={(e) => {
              e.currentTarget.classList.remove("bg-primary/5", "border-primary/30");
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove("bg-primary/5", "border-primary/30");
              if (draggedItem) {
                const draggedParent = draggedItem.path.includes("/") ? draggedItem.path.substring(0, draggedItem.path.lastIndexOf("/")) : "";
                if (draggedParent !== "") onMoveFile(draggedItem.path, "");
                setDraggedItem(null);
              }
            }}
          />
        </div>
        
        {children && (
          <div className="border-t border-border mt-2 pt-2">
            {children}
          </div>
        )}
      </ScrollArea>

      <Dialog open={newItemDialog.open} onOpenChange={(open) => setNewItemDialog({ ...newItemDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Create New {newItemDialog.type === "folder" ? "Folder" : "Note"}
            </DialogTitle>
            <DialogDescription>
              Enter a name for your new {newItemDialog.type === "folder" ? "folder" : "note"}.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
            placeholder={newItemDialog.type === "folder" ? "Folder name" : "Note name"}
            onKeyDown={(e) => e.key === "Enter" && handleCreateItem()}
            data-testid="new-item-name-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewItemDialog({ open: false, type: "file", parentPath: "" })}>
              Cancel
            </Button>
            <Button onClick={handleCreateItem} data-testid="create-item-button">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </aside>
  );
};